import * as React from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Render the error (destructive) state. */
  error?: boolean;
  /** Decoration rendered inside the field on the left (e.g. a currency sign). */
  prefix?: React.ReactNode;
  /** Decoration rendered inside the field on the right. */
  suffix?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, prefix, suffix, ...props }, ref) => {
    const input = (
      <input
        ref={ref}
        className={cn(
          'flex h-9 w-full rounded-md border bg-card px-2.5 py-2 text-[13px] outline-none transition-colors',
          'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
          error
            ? 'border-destructive focus-visible:ring-2 focus-visible:ring-destructive/40'
            : 'border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring',
          prefix != null && 'pl-7',
          suffix != null && 'pr-9',
          className,
        )}
        {...props}
      />
    );

    // Only wrap when a decoration is present, so plain inputs keep their
    // exact DOM shape.
    if (prefix == null && suffix == null) return input;

    return (
      <div className="relative">
        {prefix != null && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground">
            {prefix}
          </span>
        )}
        {input}
        {suffix != null && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
